<template>
  <div ref="container">
    <Backdrop
      v-if="showBackdrop"
      :show="showBackdrop"
      @click="hideSuggestions"
    />
    <div class="relative min-w-[244px]" :class="widthClassLookup[width]">
      <label :for="inputId">
        <button
          class="absolute top-0 h-full w-fit px-2.5 py-2"
          data-qa="submit-search"
          :aria-label="$t('search.search')"
          @click="searchQuery ? submitSearch() : inputRef?.focus()"
        >
          <span class="sr-only">{{ $t('search.search') }}</span>
          <IconSearch class="size-5" />
        </button>
      </label>

      <input
        :id="inputId"
        ref="inputRef"
        v-model="searchQuery"
        data-test-id="header-search-input"
        :placeholder="isFocused ? '' : $t('search.placeholder')"
        autocomplete="off"
        list="autocompleteOff"
        class="rounded-sm border-0 border-b border-solid bg-white px-5 py-2 pl-10 outline-gray-secondary ring-gray-secondary placeholder:text-black-80 focus:border-gray-secondary focus:text-black focus:ring-gray-secondary"
        :class="widthClassLookup[width]"
        type="search"
        data-qa="search-bar"
        @focus="handleFocus"
        @blur="handleBlur"
        @keydown.enter="submitSearch"
      />
      <button
        class="absolute right-4 top-0.5 flex p-3 transition-opacity"
        :class="{ 'opacity-0': !searchQuery }"
        :aria-label="$t('search.clear_search')"
        @click="searchQuery = ''"
      >
        <IconCloseS class="fill-current size-4 text-primary" />
      </button>
    </div>
    <!-- Desktop and Tablet Results -->
    <Flyout v-if="!isMobile" :is-open="areSearchResultsVisible">
      <div class="absolute right-0 mt-2 w-96 bg-white shadow-lg">
        <SearchResults
          v-if="showSuggestions"
          :categories="categories"
          :fetching="fetching"
          :product-suggestions="products"
          :results-count="count"
          :search-term="searchQuery"
          @on-flyout-dismiss="close"
          @click:result="close"
          @click:view-all="submitSearch"
        />
      </div>
    </Flyout>
    <!-- Mobile Results -->
    <div
      v-if="areSearchResultsVisible && showSuggestions && isMobile"
      :style="mobileResultsPosition"
      class="absolute right-0 w-full overflow-auto overscroll-contain bg-white shadow-bottom"
    >
      <SearchResults
        :categories="categories"
        :fetching="fetching"
        :product-suggestions="products"
        :results-count="count"
        :search-term="searchQuery"
        @on-flyout-dismiss="close"
        @click:result="submitSearch"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core'
import type {
  SearchEntity,
  CategorySearchSuggestion,
  ProductSearchSuggestion,
} from '@scayle/storefront-nuxt'
import useSearchProductsByOrderNumber from '~/composables/useSearchProductsByOrderNumber'
import { getInitialPricePromotionKey } from '~/utils/product'

// Missing "export" from backbone.
// extracted from: ~/node_modules/@aboutyou/backbone/types/Search.d.ts
type CategoryAttributeFilter = {
  type: 'attribute'
  attributeFilter: {
    group: {
      id: number
      key: string
      label: string
      type: string
      multiSelect: boolean
    }
    values: Array<{
      id: number
      value: string
      label: string
    }>
  }
}

defineProps({
  width: {
    type: String as PropType<'fit' | 'full'>,
    default: () => 'fit',
    validator: (value: string) => {
      return ['fit', 'full'].includes(value)
    },
  },
})
const emit = defineEmits(['change:search-results-visibility'])
const { goToAdvantagePage } = useAdvantagePage()
const { toLocalePath } = useRouteHelpers()
const { isMobileSearchBarVisible, isPdp, setProductInitialKeys } = useUiState()
const router = useRouter()
const route = useRoute()
const shop = useCurrentShop()
const { isMobile } = useDeviceType()
const scope = effectScope()
const inputId = useId()

const [
  {
    products,
    categories,
    fetching,
    searchQuery,
    count,
    hasSearchQuery,
    resetSearch,
  },
  resolveProduct,
  searchProductsByOrderNumber,
] = await Promise.all([
  useSearchResults(),
  useResolveProduct(),
  useSearchProductsByOrderNumber(),
])

const areSearchResultsVisible = computed(() =>
  Boolean(!isOrderNumber(searchQuery.value) && hasSearchQuery.value),
)

const mobileResultsPosition = ref({})
const container = ref<HTMLDivElement>()
const inputRef = ref<HTMLInputElement>()
const isFocused = ref(false)

const showSuggestions = ref(false)

const locale = computed(() => shop?.value.isoLanguageCode)

const hideSuggestions = () => {
  showSuggestions.value = false
  if (isMobile.value) {
    isMobileSearchBarVisible.value = false
  }
}

const close = () => {
  showSuggestions.value = false
  searchQuery.value = ''
  if (resetSearch) {
    resetSearch()
  }
}

const getStrippedQuery = (query: string) => {
  // Remove all non alphanumeric characters from the query
  let strippedQuery = query.toUpperCase().replace(/[^a-z0-9]/gi, '')
  // Remove all non alphabet characters from the end of the query
  strippedQuery = strippedQuery.replace(/\D*$/, '')
  return strippedQuery
}

const submitSearch = async () => {
  const query = searchQuery.value?.trim()
  const strippedQuery = getStrippedQuery(query)

  const isOrderIdSearch = isOrderNumber(strippedQuery)

  close()

  setProductInitialKeys(undefined, undefined)

  if (isOrderIdSearch) {
    const success = await goToAdvantagePage(strippedQuery)
    if (!success) {
      await goToProductByOrderNumber(strippedQuery)
    }
  } else {
    const finding = (await resolveProduct.resolve(query)) as SearchEntity

    if (finding) {
      if ((finding as CategorySearchSuggestion).categorySuggestion) {
        const path = getPathFromCategorySuggestion(finding)
        router.push(`${toLocalePath(path)}`)
      } else {
        const route = getProductDetailRouteLocalized(
          locale.value,
          (finding as ProductSearchSuggestion).productSuggestion.product,
        )
        router.push(`${toLocalePath(route)}`)
      }
    } else {
      await goToSearchPage(query, false)
    }
  }
}

const goToProductByOrderNumber = async (query: string) => {
  const { product, orderNumber } =
    (await searchProductsByOrderNumber.fetch({ term: query })) || {}

  if (product) {
    const newPromotionKey = getInitialPricePromotionKey(
      product,
      orderNumber?.toString(),
    )
    setProductInitialKeys(product.masterKey, newPromotionKey)

    const route = getProductDetailRouteLocalized(shop.value.locale, product)
    router.push(`${toLocalePath(route)}?ordernumber=${orderNumber}`)
  } else {
    goToSearchPage(query, true)
  }
}

const goToSearchPage = async (query: string, forceNoResults: boolean) => {
  await navigateTo(toLocalePath(getSearchRoute(query, forceNoResults)))
  close()
}

const widthClassLookup = {
  fit: 'w-fit',
  full: 'w-full',
}

const getMobileResultsPosition = () => {
  const windowVisualHeight = window.visualViewport?.height || window.innerHeight
  const containerBoundingRect = container.value?.getBoundingClientRect()
  if (!containerBoundingRect) {
    return {}
  }
  const { height, top } = containerBoundingRect
  return {
    top: height + 'px',
    bottom: (windowVisualHeight - height - top) * -1 + 'px',
  }
}

const adjustMobileResultsPositioning = () => {
  if (import.meta.server) {
    return
  }
  mobileResultsPosition.value = getMobileResultsPosition()
}
const debouncedAdjustMobileResultsPositioning = useDebounceFn(
  adjustMobileResultsPositioning,
  10,
)

const handleFocus = () => {
  isFocused.value = true
  requestAnimationFrame(() => {
    showSuggestions.value = true
  })
}

onMounted(() => {
  // Clear the search input field to prevent browsers from autofilling it
  searchQuery.value = ''
  adjustMobileResultsPositioning()
})

useEventListener('resize', debouncedAdjustMobileResultsPositioning)

scope.run(() => {
  useWatch(isMobileSearchBarVisible, () => {
    if (isPdp.value && isMobile.value) {
      inputRef.value?.focus()
    }
  })

  useWatch(
    areSearchResultsVisible,
    (newValue) => {
      emit('change:search-results-visibility', newValue)
      adjustMobileResultsPositioning()
    },
    { immediate: true },
  )

  useWatch(searchQuery, (query) => {
    if (isMobile.value) {
      return
    }
    showSuggestions.value = Boolean(query)
  })

  useWatch(
    () => route.fullPath,
    () => {
      if (isFocused.value) {
        inputRef.value?.blur()
      }
    },
  )
})

const showBackdrop = computed(() => {
  if (isMobile.value && isPdp.value) {
    return showSuggestions.value
  }
  if (!isMobile.value) {
    return showSuggestions.value || isFocused.value
  }
  return false
})

const handleBlur = () => {
  isFocused.value = false
  requestAnimationFrame(() => {
    const activeEl = document.activeElement as HTMLElement
    if (!container.value?.contains(activeEl)) {
      hideSuggestions()
    }
  })
}

function getPathFromCategorySuggestion(foundProdOrCat: SearchEntity) {
  const catEntity = foundProdOrCat as CategorySearchSuggestion
  const path = catEntity.categorySuggestion.category.path

  const queryString = catEntity.categorySuggestion.filters
    .map((filterEntity) => {
      const filter = filterEntity as CategoryAttributeFilter
      const key = filter.attributeFilter.group.key
      const values = filter.attributeFilter.values.map((attr) => attr.id)
      return `${key}=${values.join(',')}`
    })
    .join('&')

  return path + (queryString ? '?' + queryString : '')
}
</script>
